import { Fragment, ReactNode, useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@backstage/ui';
import { Link } from '@backstage/core-components';
import HomeIcon from '@material-ui/icons/Home';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { BreadcrumbItem } from '../../types';
import { formatDateWithOrdinal } from '../../utils';
import { ContactSupportButton } from './ContactSupportButton';

const STICKY_HEADER_HEIGHT_VAR = '--arla-sticky-header-height';

const useStyles = makeStyles({
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'var(--arla-header-bg-color)',
    borderBottom: '1px solid var(--arla-card-divider-color)',
  },
  topBar: {
    backgroundColor: 'var(--arla-bg-color)',
    height: '30px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px',
  },
  breadcrumbContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  breadcrumbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  breadcrumbLink: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    color: 'var(--arla-breadcrumb-item-color)',
    textDecoration: 'none',
    '&:hover': {
      color: 'var(--arla-breadcrumb-item-hover-color)',
    },
  },
  homeIcon: {
    fontSize: '18px',
  },
  separator: {
    fontSize: '16px',
    color: 'var(--arla-breadcrumb-separator-color)',
  },
  currentCrumb: {
    fontWeight: 600,
    color: 'var(--arla-breadcrumb-item-active-color)',
    padding: '2px 4px',
    backgroundColor: 'var(--arla-breadcrumb-item-active-background-color)',
    borderRadius: '4px',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: 1.4,
    margin: 0,
    flex: 1,
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  date: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'var(--arla-text-secondary-color)',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: '14px',
    color: 'var(--arla-text-secondary-color)',
    margin: 0,
    maxWidth: '60%',
  },
  collapsible: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease, opacity 0.25s ease, margin-top 0.3s ease',
    maxHeight: '300px',
    opacity: 1,
    marginTop: 0,
  },
  collapsibleHidden: {
    maxHeight: 0,
    opacity: 0,
    marginTop: '-20px',
  },
});

interface PageHeaderProps {
  title: string;
  titleLoading?: boolean;
  breadcrumbs: BreadcrumbItem[];
  description?: string;
  showDate?: boolean;
  extra?: ReactNode;
}

export const PageHeader = ({
  title,
  titleLoading,
  breadcrumbs,
  description,
  showDate = true,
  extra,
}: PageHeaderProps) => {
  const classes = useStyles();
  const currentDate = formatDateWithOrdinal(new Date());
  const [collapsed, setCollapsed] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const COLLAPSE_BREAKPOINT = 100;
  const EXPAND_BREAKPOINT = 60;
  const TRANSITION_LOCK_MS = 350;

  // This header is sticky and overlaps page content once scrolled, so anchor
  // links (e.g. TechDocs subheadings) land underneath it. Publish the header's
  // live height as a CSS var so the scroll container (.rootWrapper) can offset
  // scrollIntoView() via scroll-padding-top, even across the TechDocs shadow root.
  useEffect(() => {
    const node = headerRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        STICKY_HEADER_HEIGHT_VAR,
        `${node.getBoundingClientRect().height}px`,
      );
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(STICKY_HEADER_HEIGHT_VAR, '0px');
    };
  }, []);

  useEffect(() => {
    const lockedRef = { current: false };
    let unlockTimer: ReturnType<typeof setTimeout> | undefined;

    const handleScroll = (e: Event) => {
      if (lockedRef.current) return;
      const target = e.target as Element;
      if (target && typeof (target as Element).scrollTop === 'number') {
        const scrollTop = target.scrollTop;
        setCollapsed(prev => {
          let next = prev;
          if (!prev && scrollTop > COLLAPSE_BREAKPOINT) next = true;
          else if (prev && scrollTop < EXPAND_BREAKPOINT) next = false;
          if (next !== prev) {
            lockedRef.current = true;
            clearTimeout(unlockTimer);
            unlockTimer = setTimeout(() => {
              lockedRef.current = false;
            }, TRANSITION_LOCK_MS);
          }
          return next;
        })
      }
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      clearTimeout(unlockTimer);
    };
  }, []);

  return (
    <div className={classes.header} ref={headerRef}>
      <div className={classes.topBar} />
      <div className={classes.body}>
        <div className={classes.breadcrumbContainer}>
          <div className={classes.breadcrumbRow}>
            <Link to="/" className={classes.breadcrumbLink}>
              <HomeIcon className={classes.homeIcon} />
            </Link>
            {breadcrumbs.map((item, index) => (
              <Fragment key={index}>
                <ChevronRightIcon className={classes.separator} />
                {item.loading && <Skeleton width={100} height={20} rounded />}
                {!item.loading && (item.path && index < breadcrumbs.length - 1 ? (
                  <Link to={item.path} className={classes.breadcrumbLink}>
                    {item.label}
                  </Link>
                ) : (
                  <span className={classes.currentCrumb}>{item.label}</span>
                ))}
              </Fragment>
            ))}
          </div>
          <ContactSupportButton text="Support or inquiries" contactEmail="PlatformsDeveloperEnablement@arlafoods.com" />
        </div>

        <div className={`${classes.collapsible} ${collapsed ? classes.collapsibleHidden : ''}`}>
          <div className={classes.titleRow}>
            {titleLoading ? (
              <Skeleton width={220} height={32} rounded className={classes.title} />
            ) : (
              <h1 className={classes.title}>{title}</h1>
            )}
            <div className={classes.rightSide}>
              {extra}
              {showDate && <span className={classes.date}>{currentDate}</span>}
            </div>
          </div>

          {description && <p className={classes.description}>{description}</p>}
        </div>
      </div>
    </div>
  );
};
